import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ChevronLeft, Share2, Copy, MessageCircle, X, LogOut } from "lucide-react";
import { api, formatError, fileUrl } from "@/lib/api";
import { LogoMark } from "@/components/LogoMark";

function ShareRow({ product }) {
  const url = window.location.href;
  const text = `Check out ${product.name} (${product.gi_name}) on GI Bharat: ${url}`;
  const openWA = () => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  const openFB = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, "_blank");
  const openX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, "_blank");
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text, url }); return; } catch {}
    }
    copy();
  };
  return (
    <div className="pd-share" data-testid="pd-share">
      <span>Share:</span>
      <button title="WhatsApp" onClick={openWA} data-testid="share-whatsapp"><MessageCircle size={16} /></button>
      <button title="Facebook" onClick={openFB} data-testid="share-facebook">f</button>
      <button title="X / Twitter" onClick={openX} data-testid="share-x">𝕏</button>
      <button title="Copy link" onClick={copy} data-testid="share-copy"><Copy size={14} /></button>
      <button title="Share" onClick={share} data-testid="share-native"><Share2 size={14} /></button>
    </div>
  );
}

function CheckoutModal({ product, onClose, onDone }) {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "", buyer_address: "", quantity: 1, notes: "" });
  const [busy, setBusy] = useState(false);
  const total = Number(product.final_price) * Number(form.quantity || 1);

  useEffect(() => {
    if (!localStorage.getItem("tv_token")) return;
    api.get("/auth/me").then(r => {
      setMe(r.data);
      setForm(f => ({ ...f, buyer_name: r.data.name || f.buyer_name, buyer_email: r.data.email && !r.data.email.endsWith("@truevision.local") ? r.data.email : f.buyer_email, buyer_phone: r.data.phone || f.buyer_phone }));
    }).catch(() => {});
  }, []);

  const place = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api.post("/orders", { product_id: product.id, ...form, quantity: Number(form.quantity) });
      toast.success("Order placed. SMS confirmation on its way.");
      onDone(r.data.order);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="modal-mask" onClick={onClose} data-testid="checkout-modal">
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
          <div>
            <p className="eyebrow" style={{ margin: 0 }}>CASH ON DELIVERY</p>
            <h3>Complete your order</h3>
          </div>
          <button className="icon-btn" onClick={onClose} data-testid="checkout-close" aria-label="Close"><X size={20} /></button>
        </div>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>You'll receive an SMS with the order status. Card / UPI payments are coming soon.</p>
        <form onSubmit={place}>
          <div className="field-grid">
            <label>Full name<input required value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} data-testid="checkout-name" /></label>
            <label>Phone number<input required value={form.buyer_phone} onChange={e => setForm({ ...form, buyer_phone: e.target.value })} placeholder="+91…" data-testid="checkout-phone" /></label>
          </div>
          <label>Email<input required type="email" value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} data-testid="checkout-email" /></label>
          <label>Delivery address<textarea required value={form.buyer_address} onChange={e => setForm({ ...form, buyer_address: e.target.value })} data-testid="checkout-address" placeholder="Full address with PIN" /></label>
          <div className="field-grid">
            <label>Quantity<input required type="number" min="1" value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} data-testid="checkout-quantity" /></label>
            <label>Order total<input readOnly value={`₹${total.toLocaleString("en-IN")}`} data-testid="checkout-total" /></label>
          </div>
          <label>Notes for seller (optional)<textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" /></label>
          <button className="primary-btn" style={{ width: "100%" }} disabled={busy} data-testid="checkout-submit">
            {busy ? "Placing order…" : `Place order · ₹${total.toLocaleString("en-IN")} COD`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [mediaIdx, setMediaIdx] = useState(0);
  const [showCheckout, setShowCheckout] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(r => setData(r.data))
      .catch(err => setError(formatError(err)));
    if (localStorage.getItem("tv_token")) {
      api.get("/auth/me").then(r => setMe(r.data)).catch(() => {});
    }
  }, [id]);

  if (error) {
    return (
      <main className="storefront">
        <header className="store-header" style={{ padding: "10px 4vw" }}>
          <Link to="/" className="store-brand"><LogoMark small /><span>GI Bharat<small>GI marketplace</small></span></Link>
        </header>
        <div className="pd-shell">
          <Link to="/" className="pd-back"><ChevronLeft size={14} /> Back to marketplace</Link>
          <div className="error" data-testid="pd-error">{error}</div>
        </div>
      </main>
    );
  }
  if (!data) return <main className="storefront"><div className="pd-shell" data-testid="pd-loading">Loading product…</div></main>;

  const { product, seller } = data;
  const media = [...(product.images || []).map(p => ({ path: p, kind: "image" })), ...(product.videos || []).map(p => ({ path: p, kind: "video" }))];
  const currentMedia = media[mediaIdx];

  return (
    <main className="storefront">
      <header className="store-header">
        <Link to="/" className="store-brand"><LogoMark small /><span>GI Bharat<small>GI marketplace</small></span></Link>
        {me
          ? (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
              <Link to={me.role === "admin" ? "/admin" : me.role === "buyer" ? "/orders" : "/"} style={{ color: "var(--tv-green)", fontWeight: 700, fontSize: 13, textDecoration: "none" }} data-testid="pd-my-account-link">Hi, {me.name.split(" ")[0]}</Link>
              <button className="secondary-btn" data-testid="pd-signout-button" onClick={() => { localStorage.removeItem("tv_token"); window.location.reload(); }}><LogOut size={13} /> Sign out</button>
            </div>
          )
          : <Link to="/login" className="store-login" style={{ marginLeft: "auto" }} data-testid="pd-login-link">Sign in</Link>}
      </header>

      <div className="pd-shell">
        <Link to="/" className="pd-back" data-testid="pd-back-link"><ChevronLeft size={14} /> Back to marketplace</Link>

        <div className="pd-layout">
          <div className="pd-gallery" data-testid="pd-gallery">
            <div className="pd-main-image">
              {currentMedia
                ? (currentMedia.kind === "video"
                    ? <video src={fileUrl(currentMedia.path)} controls playsInline />
                    : <img src={fileUrl(currentMedia.path)} alt={product.name} />)
                : <span style={{ color: "var(--saffron)", fontSize: 60 }}>✦</span>}
            </div>
            {media.length > 1 && (
              <div className="pd-thumbs">
                {media.map((m, i) => (
                  <button key={m.path} className={i === mediaIdx ? "active" : ""} onClick={() => setMediaIdx(i)} data-testid={`pd-thumb-${i}`}>
                    {m.kind === "video" ? <video src={fileUrl(m.path)} muted /> : <img src={fileUrl(m.path)} alt="" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pd-info" data-testid="pd-info">
            <span className="gi">{product.gi_name}</span>
            <h1 data-testid="pd-name">{product.name}</h1>
            {product.verified
              ? <span className="verified"><ShieldCheck size={13} /> Verified GI authorised seller</span>
              : <span className="verified pending"><ShieldCheck size={13} /> Seller pending verification</span>}

            <div className="pd-price">
              <span className="now" data-testid="pd-final-price">₹{Number(product.final_price).toLocaleString("en-IN")}</span>
              {product.discount_pct > 0 && (
                <>
                  <span className="mrp">₹{Number(product.mrp).toLocaleString("en-IN")}</span>
                  <span className="off">{product.discount_pct}% off</span>
                </>
              )}
            </div>
            <p className="pd-gst">Inclusive of {product.gst_pct}% GST · per {product.unit}</p>

            {product.description && <p style={{ color: "var(--muted)", fontSize: 14 }}>{product.description}</p>}

            <div className="pd-specs" data-testid="pd-specs">
              {product.color && <div className="row"><b>Colour</b><span>{product.color}</span></div>}
              {product.size && <div className="row"><b>Size</b><span>{product.size}</span></div>}
              {product.stock > 0 && <div className="row"><b>In stock</b><span>{product.stock} {product.unit}</span></div>}
              {(product.specifications || []).map((s, i) => (
                <div className="row" key={i}><b>{s.label}</b><span>{s.value}</span></div>
              ))}
            </div>

            <div className="pd-actions">
              <button className="primary-btn" data-testid="pd-buy-button" onClick={() => setShowCheckout(true)} disabled={product.stock === 0}>
                {product.stock === 0 ? "Out of stock" : "Buy now (COD)"}
              </button>
              <Link to={`/verify/${product.id}`} className="secondary-btn" data-testid="pd-verify-link"><ShieldCheck size={14} /> Verify authenticity</Link>
            </div>

            <ShareRow product={product} />

            {seller && (
              <Link to={`/seller/${product.seller_id}`} className="seller-strip" data-testid="pd-seller-strip" style={{ textDecoration: "none", color: "inherit" }}>
                <div className="av">{seller.profile_photo ? <img src={fileUrl(seller.profile_photo)} alt="" /> : "✦"}</div>
                <div>
                  <b>{seller.firm_name || product.seller_name}</b>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{seller.address || "GI authorised user"}</div>
                  <div style={{ fontSize: 12, color: "var(--tv-green)", fontWeight: 700 }}>View seller profile →</div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </div>

      {showCheckout && !placed && (
        <CheckoutModal product={product} onClose={() => setShowCheckout(false)} onDone={o => { setPlaced(o); setShowCheckout(false); }} />
      )}
      {placed && (
        <div className="modal-mask" onClick={() => { setPlaced(null); navigate("/"); }} data-testid="order-confirm-modal">
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{ textAlign: "center" }}>
            <ShieldCheck size={40} style={{ color: "var(--india-green)" }} />
            <h3 style={{ margin: "12px 0 6px" }}>Order placed</h3>
            <p style={{ color: "var(--muted)" }}>Order <b>#{placed.id.slice(0, 8)}</b> — ₹{Number(placed.total).toLocaleString("en-IN")} (COD).</p>
            <p style={{ color: "var(--muted)", fontSize: 13 }}>An SMS confirmation has been sent to <b>{placed.buyer_phone}</b>. The seller will contact you shortly.</p>
            <button className="primary-btn" style={{ marginTop: 14 }} onClick={() => navigate("/")} data-testid="order-close-button">Continue shopping</button>
          </div>
        </div>
      )}
    </main>
  );
}
